import { useEffect, useState } from 'react';
import { coreAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';

/**
 * The name the Create RFQ form shows in its read-only "Customer" box: the
 * company the new RFQ will be filed under (CR-6171).
 *
 * The box used to show the user's home site whatever site was in view, while
 * the RFQ landed on the site in view. Where it lands is the backend's call: the
 * site in view when the user may act for it, else their home company. The view
 * context answers that as `active_company_name`, from the same resolver the
 * create uses, and it is asked again whenever the top-bar view changes (a
 * switch is saved before the view key moves, so the answer is for the new
 * view). A Helix admin raises the RFQ for the company picked in the top bar,
 * so that name is shown for them.
 *
 * @example
 *   const customerName = useRfqCustomerName();
 *   <Input value={customerName || '—'} disabled readOnly />
 * @returns The customer name, or '' while it is not known for the current view
 * @link RFQCreateWizard
 */
export function useRfqCustomerName(): string {
  const { user } = useAuth();
  const { selectedCompanyName, viewKey } = useView();
  const isHelixAdmin = user?.company_type === 'helix';
  const [filedUnder, setFiledUnder] = useState<{ viewKey: string; name: string } | null>(null);

  useEffect(() => {
    if (isHelixAdmin) return undefined;
    let cancelled = false;
    coreAPI.getViewContext()
      .then((res) => {
        if (!cancelled) setFiledUnder({ viewKey, name: res.data.active_company_name ?? '' });
      })
      .catch((err: unknown) => {
        console.error('Error loading the RFQ customer:', err);
      });
    return () => { cancelled = true; };
  }, [isHelixAdmin, viewKey]);

  if (isHelixAdmin) return selectedCompanyName ?? '';
  // Never show the previous view's answer while the new one is on its way.
  return filedUnder && filedUnder.viewKey === viewKey ? filedUnder.name : '';
}
